import { StoreState } from '@/core/models/store.interface';
import { Module } from 'vuex';
import { SquadFilter } from './squad-filter.interface';
import squadFilterService from './squad-filter.service';

export interface SquadFilterState {
   current_squad_filter_loading: boolean;
   squad_filters: Array<SquadFilter>;
   active_squad_filter?: SquadFilter;
}

const SquadFilterStore: Module<SquadFilterState, StoreState> = {
   namespaced: true,
   state: {
      permissions: null,
      layers: null,
      current_squad_filter_loading: false,
      squad_filters: [],
   },

   getters: {},

   mutations: {
      setSquadFilters(state, squad_filters) {
         state.squad_filters = squad_filters;
      },
      addSquadFilterToSquadFilters(state, filter: SquadFilter) {
         const FILTER_ID_EXISTS = state.squad_filters.some((squad_filter) => squad_filter.id === filter.id);
         if (FILTER_ID_EXISTS) return;

         const index = state.squad_filters.findIndex((sf) => sf.id == undefined);

         if (index < 0) state.squad_filters.unshift(filter);
         else state.squad_filters[index] = filter;
      },
      deleteSquadFilterFromSquadFilters(state, squad_filter_id: number) {
         const index = state.squad_filters.findIndex((sf) => sf.id == squad_filter_id);
         if (index < 0) return;

         state.squad_filters.splice(index, 1);
      },
      UPDATE_SQUAD_FILTER(
         state,
         { squad_filter_id, name, filter, shift_amount }: { squad_filter_id: number; name: string; filter: any; shift_amount: number },
      ) {
         const INDEX_OF_SQUAD_TO_UPDATE = state.squad_filters.findIndex((squad) => squad.id === squad_filter_id);
         if (INDEX_OF_SQUAD_TO_UPDATE < 0) return;

         if (name) state.squad_filters[INDEX_OF_SQUAD_TO_UPDATE]['name'] = name;
         if (filter) state.squad_filters[INDEX_OF_SQUAD_TO_UPDATE]['filter'] = filter;
         if (shift_amount || shift_amount === 0) {
            state.squad_filters[INDEX_OF_SQUAD_TO_UPDATE].fill_rate['timeregistration_shift_amount_needed'] =
               state.squad_filters[INDEX_OF_SQUAD_TO_UPDATE].fill_rate.shift_amount_filled;
            state.squad_filters[INDEX_OF_SQUAD_TO_UPDATE].fill_rate['timeregistration_shift_amount_filled'] = shift_amount;
         }
      },
      SET_CURRENT_SQUAD_FILTER_LOADING(state, is_loading: boolean) {
         state.current_squad_filter_loading = is_loading;
      },
   },

   actions: {
      async getByBoardId(
         { commit },
         {
            board_id,
            fill_rate_from,
            fill_rate_to,
            only_planning_confirmation = false,
            view,
         }: { board_id: number; fill_rate_from?: string; fill_rate_to?: string; only_planning_confirmation: boolean; view: string },
      ) {
         if (!board_id) return;

         return await squadFilterService
            .getByBoardId(board_id, only_planning_confirmation, fill_rate_from, fill_rate_to, view)
            .then((response) => {
               commit('setSquadFilters', response.data);

               return response;
            });
      },
      async post(
         context,
         {
            squad_filter_id,
            board_id,
            name,
            filter,
            fill_rate_from,
            fill_rate_to,
         }: { squad_filter_id: number; board_id: number; name: string; filter: string; fill_rate_from?: string; fill_rate_to?: string },
      ) {
         const BODY = {
            id: squad_filter_id,
            board_id,
            name,
            fill_rate_from,
            fill_rate_to,
            filter: filter ?? context.rootGetters['board/getBoardFilterAsString'],
         } as SquadFilter;
         return await squadFilterService.post(BODY).then((response) => {
            context.commit('addSquadFilterToSquadFilters', response);
            return response;
         });
      },
      async patch(context, { squad_filter_id, path, value }: { squad_filter_id: number; path: string; value: any }) {
         const BODY = { op: 'replace', path, value };
         return await squadFilterService.patch(squad_filter_id, [BODY]).then((response) => {
            context.commit('UPDATE_SQUAD_FILTER', {
               name: response.name,
               squad_filter_id: response.id,
               filter: response.filter,
            });

            return response;
         });
      },
      async delete(context, squad_filter_id: number) {
         if (!squad_filter_id) return;

         return await squadFilterService.delete(squad_filter_id).then((response) => {
            context.commit('deleteSquadFilterFromSquadFilters', squad_filter_id);
            return response;
         });
      },
   },
};

export default SquadFilterStore;
